import React from 'react';
import { graphql } from 'gatsby';
import classNames from 'classnames';
import track from 'react-tracking';
import { Helmet } from 'react-helmet-async';

import paragonBreakpoints from '@edx/paragon/dist/utils/breakpoints';
import { ContentfulImageDefaultProps, ContentfulImagePropTypes } from './propTypes';
import { addContentfulImageParams, generateUrlForBreakpoints, generateSrcSet } from './utils';

function RawImage({
  className, imgUrl, lazyLoad, description, title, width, height,
}) {
  return (
    <img
      className={classNames('ContentfulImage', className)}
      src={imgUrl}
      loading={lazyLoad ? 'lazy' : 'eager'}
      alt={description || title || ''}
      width={width}
      height={height}
    />
  );
}

function ContentfulImage(props) {
  if (!props?.url) {
    return null;
  }

  const {
    url,
    width,
    height,
    additionalParams,
    breakpoints,
    isLcp,
  } = props;

  // Contentful image API only accepts number for w/h params
  const params = {
    w: typeof width === 'number' && width,
    h: typeof height === 'number' && height,
    ...additionalParams,
  };

  const format = additionalParams?.fm || 'webp';
  const quality = additionalParams?.q || 75;
  const imgUrl = addContentfulImageParams(url, { ...params, fm: format, q: quality });

  const breakpointsWithUrl = generateUrlForBreakpoints(breakpoints, url, format);
  const srcSet = generateSrcSet(breakpointsWithUrl);

  return (
    <>
      {isLcp && imgUrl && (
        <Helmet>
          <link href={imgUrl} as="image" />
          {breakpointsWithUrl?.at(0) && breakpointsWithUrl.map(({ url: src, type }) => {
            let mediaQuery = `(min-width: ${paragonBreakpoints[type].minWidth}px) and (max-width: ${paragonBreakpoints[type].maxWidth}px)`;
            if (!paragonBreakpoints[type].minWidth) {
              mediaQuery = `(max-width: ${paragonBreakpoints[type].maxWidth})`;
            }
            if (!paragonBreakpoints[type].maxWidth) {
              mediaQuery = `(min-width: ${paragonBreakpoints[type].minWidth})`;
            }
            return (
              <link href={src} as="image" media={mediaQuery} />
            );
          })}
        </Helmet>
      )}

      {srcSet && (
        <picture>
          <source srcSet={srcSet} />
          <RawImage {...props} imgUrl={imgUrl} />
        </picture>
      )}

      {!srcSet && (
        <RawImage {...props} imgUrl={imgUrl} />
      )}

    </>
  );
}

RawImage.propTypes = ContentfulImagePropTypes;
ContentfulImage.propTypes = ContentfulImagePropTypes;

RawImage.defaultProps = ContentfulImageDefaultProps;
ContentfulImage.defaultProps = ContentfulImageDefaultProps;

// @todo: use this fragment instead of contentfulContentModel.js after
// we move to template-scope queries instead of context-scope queries
export const ContentfulImageFragment = graphql`
  fragment ContentfulImageFragment on ContentfulAsset {
    id
    url
    width
    height
    description
    title
  }
`;

export default track({
  component: 'contentful-image',
})(ContentfulImage);
